import { useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { receiveKeys } from './keys';

import type {
  TypeApiResponse,
  TypeGetRequestsRequestedResponse,
  TypeReceiveDetailOrderItem,
} from '~/types/receive.types';

type RequestsRequestedParams = {
  ownerCode: string;
  requestId: string;
  enabled?: boolean;
};

/** 변환된 응답 타입 */
type FormattedResponse = Omit<TypeGetRequestsRequestedResponse, 'orderItems'> & {
  orderItems: TypeReceiveDetailOrderItem[];
};

/**
 * [입고요청관리] 입고요청 상세 조회 API
 */
const fetchGetRequestsRequested = async (ownerCode: string, requestId: string) => {
  const endpoint = '/api-inbound/v1/3pl/receiving';
  const { data } = await axiosInstance.get<TypeApiResponse<TypeGetRequestsRequestedResponse>>(
    `${endpoint}/${ownerCode}/requests/requested/${requestId}`
  );
  return data.data;
};

/**
 * orderItems를 TypeReceiveDetailOrderItem 형태로 변환
 */
const transformResponse = (data: TypeGetRequestsRequestedResponse): FormattedResponse => {
  const formattedItems: TypeReceiveDetailOrderItem[] = data.orderItems.map((item, index) => {
    const isUsingDate = !!(item.expirationDate ?? item.manufactureDate);
    const dateType = item.expirationDate ? 'expirationDate' : 'manufactureDate';
    const date = item.expirationDate ?? item.manufactureDate;

    return {
      index: index + 1,
      itemId: item.itemId,
      goodsId: item.goodsId,
      goodsCode: item.goodsCode,
      nsid: item.nsid,
      alternativeCode: item.alternativeCode,
      goodsName: item.goodsName,
      kurlyKeepTypeDesc: item.kurlyKeepTypeDesc,
      dateUseYn: isUsingDate,
      dateType: isUsingDate ? dateType : null,
      date: isUsingDate ? date : null,
      usePallet: item.usePallet,
      lotNo: item.lotNo ?? '',
      boxQty: item.boxQty,
      boxPerQty: item.boxPerQty,
      eachQty: item.eachQty,
      totalQty: item.totalQty,
    };
  });

  return { ...data, orderItems: formattedItems };
};

/**
 * [입고요청관리] 입고요청 상세 조회 Query Hook
 * @returns 변환된 응답 타입 (FormattedResponse)
 */
export const useGetRequestsRequestedQuery = ({ ownerCode, requestId, enabled = true }: RequestsRequestedParams) => {
  return useQuery({
    queryKey: receiveKeys.requestsRequestedDetail(ownerCode, requestId),
    queryFn: () => fetchGetRequestsRequested(ownerCode, requestId),
    select: transformResponse,
    enabled,
    staleTime: 0,
    gcTime: 0,
  });
};
